import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { notification } from "antd";
import axios from "axios";
import {
  COMPANY_CHAT_ALERTS_URL,
  COMPANY_CHAT_INBOX_URL,
  COMPANY_CHAT_MARK_READ_URL,
} from "../utils/URLS";
import { useUserContext } from "./UserContext";

const AFTER_ID_KEY = "company_chat_alert_after_id";
const POLL_MS = 15000;

const ChatAlertsContext = createContext({
  unreadByCompany: {},
  totalUnread: 0,
  activeChatCompanyId: null,
  setActiveChatCompanyId: () => {},
  refreshInbox: async () => {},
  markCompanyRead: async () => {},
});

function authHeaders() {
  return {
    Authorization: `Token ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  };
}

export function ChatAlertsProvider({ children }) {
  const [user] = useUserContext();
  const isStaff = Boolean(user?.user?.is_staff);
  const [api, contextHolder] = notification.useNotification();
  const [unreadByCompany, setUnreadByCompany] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeChatCompanyId, setActiveChatCompanyId] = useState(null);
  const seededRef = useRef(false);
  const activeChatRef = useRef(null);
  const shownToastIdsRef = useRef(new Set());

  useEffect(() => {
    activeChatRef.current = activeChatCompanyId;
  }, [activeChatCompanyId]);

  const refreshInbox = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || !user || !user?.user?.is_staff) return;
    try {
      const { data } = await axios.get(COMPANY_CHAT_INBOX_URL, {
        headers: authHeaders(),
      });
      const map = {};
      (data.companies || []).forEach((row) => {
        map[row.company_id] = row.unread_count || 0;
      });
      setUnreadByCompany(map);
      setTotalUnread(data.total_unread || 0);
    } catch {
      /* ignore transient poll errors */
    }
  }, [user]);

  const markCompanyRead = useCallback(
    async (companyId) => {
      if (!companyId || !user?.user?.is_staff) return;
      try {
        await axios.post(
          COMPANY_CHAT_MARK_READ_URL,
          { company_id: companyId },
          { headers: authHeaders() },
        );
        await refreshInbox();
      } catch {
        /* ignore */
      }
    },
    [refreshInbox, user],
  );

  const pollAlerts = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || !user || !user?.user?.is_staff) return;

    let afterId = Number(sessionStorage.getItem(AFTER_ID_KEY) || 0);
    if (!Number.isFinite(afterId) || afterId < 0) afterId = 0;

    try {
      const { data } = await axios.get(COMPANY_CHAT_ALERTS_URL, {
        params: { after_id: afterId },
        headers: authHeaders(),
      });
      const maxId = Number(data.max_id || afterId) || afterId;
      const rows = Array.isArray(data.messages) ? data.messages : [];

      // First successful poll: advance cursor without flooding historical toasts.
      if (!seededRef.current && afterId === 0) {
        sessionStorage.setItem(AFTER_ID_KEY, String(maxId));
        seededRef.current = true;
        await refreshInbox();
        return;
      }
      seededRef.current = true;

      rows.forEach((msg) => {
        if (!msg?.id || shownToastIdsRef.current.has(msg.id)) return;
        shownToastIdsRef.current.add(msg.id);
        if (activeChatRef.current && msg.company_id === activeChatRef.current) {
          return;
        }
        const preview = (msg.body || "").trim();
        api.open({
          key: `company-chat-${msg.id}`,
          message: msg.company_name || "New message",
          description: (
            <div>
              <div className="text-xs text-slate-500">{msg.user_name}</div>
              <div className="mt-1 text-sm text-slate-800">
                {preview.length > 140 ? `${preview.slice(0, 140)}…` : preview}
              </div>
            </div>
          ),
          placement: "topRight",
          duration: 8,
          closable: true,
        });
      });

      if (maxId > afterId) {
        sessionStorage.setItem(AFTER_ID_KEY, String(maxId));
      }
      if (rows.length) {
        await refreshInbox();
      }
    } catch {
      /* ignore */
    }
  }, [api, refreshInbox, user]);

  useEffect(() => {
    if (!user || !isStaff) return undefined;
    seededRef.current = Number(sessionStorage.getItem(AFTER_ID_KEY) || 0) > 0;
    refreshInbox();
    pollAlerts();
    const id = setInterval(pollAlerts, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") {
        pollAlerts();
        refreshInbox();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, isStaff, pollAlerts, refreshInbox]);

  const value = useMemo(
    () => ({
      unreadByCompany,
      totalUnread,
      activeChatCompanyId,
      setActiveChatCompanyId,
      refreshInbox,
      markCompanyRead,
    }),
    [
      unreadByCompany,
      totalUnread,
      activeChatCompanyId,
      refreshInbox,
      markCompanyRead,
    ],
  );

  return (
    <ChatAlertsContext.Provider value={value}>
      {contextHolder}
      {children}
    </ChatAlertsContext.Provider>
  );
}

export function useChatAlerts() {
  return useContext(ChatAlertsContext);
}
